<template>
    <div>
        <div class="grid-fitted grid-unstackable align-items-center stack-s">
            <div class="grid-item inline-m">
                <h3>
                    <span class="inline-s">{{ plan.name }}</span>
                </h3>
            </div>
            <div v-if="!hideStatus" class="grid-item no-flex inline-m">
                <r-badge v-if="plan.isActive" type="positive">Active</r-badge>
                <r-badge v-else type="negative">Inactive</r-badge>
            </div>
            <slot name="actions"></slot>
        </div>
        <div class="stack-m">
            <table class="table table-definition table-compact table-collapsed table-v-alignment-top">
                <colgroup>
                    <col style="width:110px;">
                    <col>
                </colgroup>
                <tr>
                    <td>ID</td>
                    <td v-fs-block>{{ plan.id }}</td>
                </tr>
                <tr>
                    <td>Currency</td>
                    <td v-fs-block>{{ plan.currency }}</td>
                </tr>
                <tr>
                    <td>{{ plan.pricing.formula | capitalize }}</td>
                    <td>
                        <p v-for="(price, priceIdx) in pricing" :key="priceIdx">{{ price }}</p>
                    </td>
                </tr>
                <tr v-if="invoiceTimeShift.issueTimeShift || invoiceTimeShift.dueTimeShift">
                    <td>Invoice time shift</td>
                    <td>
                        <invoice-time-shift-preview :model="invoiceTimeShift"/>
                    </td>
                </tr>
                <tr v-if="periodAnchor">
                    <td>Service anchor</td>
                    <td>
                        <recurring-interval-preview :model="periodAnchor"/>
                    </td>
                </tr>
                <template v-if="plan.hasCustomFields">
                    <tr
                        v-for="field in customFields"
                        :key="field.name"
                        :title="field.name"
                        v-fs-block>
                        <td>{{field.name}}</td>
                        <td>{{field.value}}</td>
                    </tr>
                </template>
            </table>
        </div>
        <div class="grid-fitted align-items-top">
            <div class="grid-item">
                <table class="table table-definition table-compact table-collapsed table-v-alignment-top">
                    <colgroup>
                        <col style="width:73px;">
                        <col>
                    </colgroup>
                    <tr>
                        <td>Trial</td>
                        <td v-fs-block>{{ trial | coverEmpty }}</td>
                    </tr>
                    <tr>
                        <td>Setup</td>
                        <td v-fs-block>{{ setup | coverEmpty }}</td>
                    </tr>
                </table>
            </div>
            <div class="grid-item text-right" v-if="!hidePlanTime">
                <p class="text-muted">Created on {{ plan.createdTime | dateTime }}</p>
                <p class="text-muted">Updated on {{ plan.updatedTime | dateTime }}</p>
            </div>
        </div>
    </div>
</template>

<script>
    import PlanModel from '@/common/models/plan-model';
    import ProductModel from '@/common/models/product-model';
    import PricingRenderer from '@/modules/plans/pricing-renderer';
    import CustomFieldsValuesMixin from '@/mixins/custom-fields-values-mixin';
    import InvoiceTimeShiftPreview from '@/components/billing-schedule/invoice-time-shift-preview';
    import RecurringIntervalPreview from '@/components/billing-schedule/recurring-interval-preview';


    export default {
        mixins: [CustomFieldsValuesMixin],
        name: 'plan-details-table',
        components: {
            InvoiceTimeShiftPreview,
            RecurringIntervalPreview,
        },
        props: {
            plan: {
                type: PlanModel,
            },
            product: {
                type: ProductModel,
            },
            hidePlanTime: {
                type: Boolean,
                default: false,
            },
            hideStatus: {
                type: Boolean,
                default: false,
            },
            plansCustomFields: Array,
        },
        computed: {
            pricing() {
                return PricingRenderer.render(
                    this.plan.currency,
                    this.plan.pricing,
                    this.plan.recurringInterval,
                    this.product.unitLabel,
                );
            },
            trial() {
                const {currency, trial} = this.plan;

                if (!trial || trial.length <= 0) {
                    return null;
                }

                const {period: {length, unit}, price} = this.plan.trial;
                const {pluralize, monetize} = this.$filters;

                return price
                    ? `${length} ${pluralize(unit, length)} ${monetize(price, currency)}`
                    : `${length} ${pluralize(unit, length)} - Free`;
            },
            setup() {
                const {currency, setup} = this.plan;

                if (!setup) {
                    return null;
                }

                const {price} = setup;
                const {monetize} = this.$filters;

                return price
                    ? `${monetize(price, currency)}`
                    : `Free`;
            },
            customFields() {
                return this.getCustomFields('plan', this.getCustomFieldsTypes(this.plansCustomFields));
            },
            invoiceTimeShift() {
                return this.plan.invoiceTimeShift;
            },
            periodAnchor() {
                if (this.plan.recurringInterval) {
                    return this.plan.recurringInterval.periodAnchorInstruction;
                }
                return null;
            },
        },
    };
</script>
